import React, { useContext, useMemo, useCallback } from 'react';
import { BaseCompAction, capitalizeAll, getErrorMessage, SlideOutComp } from '@moderepo/mode-app-components';
import { DialogResponse, DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, selectCurrentUsageMode, selectIsEditingForm, showDialog,
    showErrorNotification, showSlideOutPanel, showSuccessNotification, SlideOutPanelLocation, UsageMode } from '@moderepo/mode-ui-state';
import { makeStyles, Theme } from '@material-ui/core';
import { ritzDataContext, selectLoggedInAgent, selectProjectAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import clsx from 'clsx';
import { ApiError, Entity, AppAPI } from '@moderepo/mode-apis';
import { clearEntities, UIFetchEntityFilters, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { Agent, AgentRole } from '@moderepo/mode-internal-apis';
import { useNavigate } from 'react-router-dom';
import { EntityInfoContainer, EntityInfoContainerProps } from './EntityInfoContainer';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectEntity, useSelectEntityChildren } from '../../utils/dataStateHooks';
import { ENTITY_DEVICE_ID_ATTRIBUTE } from './utils';
import { EntityPreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { RouteHelper } from '../routes/RouteHelper';



const useStyle = makeStyles((theme: Theme) => {
    return {
        secondaryAction: {
            '& .icon': {
                color: theme.palette.primary.main,
            },
            '&.delete': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});


interface EntityPreviewContainerProps {
    readonly projectId: number;

    // entityId is undefined means the user is in CREATE entity mode.
    readonly entityId?: string | undefined;

    // For creating a new Entity which a homeId is already provided and the user does not need to select a home
    readonly preselectedHomeId?: number | undefined;

    // For creating a new Entity which a homeId AND parentId is already provided and the user does not need to select a home or a parent
    readonly preselectedParentId?: string | undefined;

    // Whether or not to show the Create Child button to allow the user creating entity child. The default is FALSE.
    readonly enableCreateChildren?: boolean | undefined;

    // Whether or not to show the Delete Entity button to allow the user creating entity child. The default is FALSE.
    readonly enableDeleteEntity?: boolean | undefined;

    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
    }

    // OPTIONAL handler to handle the click event on the "Show Entity Details" button.
    readonly onShowEntityDetailsHandler?: ((entity: Entity)=> void) | undefined;
}


/**
 * This Component is used for displaying an Entity in the preview panel for preview
 */
export const EntityPreviewContainer: React.FC<EntityPreviewContainerProps> = (props: EntityPreviewContainerProps) => {
    const { projectId } = props;
    const isNewEntity = !props.entityId;
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    // Get the logged in Agent data for the given project. This is is not the same as loggedInAgent data.
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const usageMode = selectCurrentUsageMode(uiState);
    const canEditEntity = projectAgent?.role === AgentRole.ADMIN && usageMode === UsageMode.MANAGE_DATA;
    const isEditing = selectIsEditingForm(uiState);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const navigate = useNavigate();


    // Get the entity object for the specified entityId
    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);


    // Create and cache the filters to be passed to the "useSelectEntityChildren" hook. We don't want to create this object dynamically
    // every time we call the "useSelectEntityChildren" because that will cause it to make unnecessary API calls.
    const fetchEntitiesFilters = useMemo((): UIFetchEntityFilters => {
        return {
            pageSize: 1,
        };
    }, []);

    const children = useSelectEntityChildren(props.projectId, props.entityId, fetchEntitiesFilters);

    const selectedEntityHasChildren = useMemo(() => {
        return children && children.items.length > 0;
    }, [children]);


    // Get the deviceId of the selected entity
    const selectedDeviceId = useMemo(() => {
        const currentDeviceId = selectedEntity?.attributes?.[ENTITY_DEVICE_ID_ATTRIBUTE];
        if (typeof currentDeviceId === 'number' && Number.isInteger(currentDeviceId)) {
            return currentDeviceId;
        }
        return undefined;
    }, [selectedEntity]);



    /**
     * Unselect the selected entity
     */
    const unselectEntity = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);


    /**
     * We need to be notified by the EntityInfoContainer when a new entity is created/canceled so that we can hide this panel
     */
    const createEntityHandlers = useMemo((): EntityInfoContainerProps['createEntityHandlers'] => {
        return {
            onEntityCreated: () => {
                uiDispatch(hideSlideOutPanel());
            },

            onCreateEntityCanceled: () => {
                uiDispatch(hideSlideOutPanel());
            },
        };
    }, [uiDispatch]);



    /**
     * On Show Entity Details button clicked
     */
    const onShowEntityDetails = useCallback((entity: Entity) => {
        if (props.onShowEntityDetailsHandler) {
            props.onShowEntityDetailsHandler(entity);
            uiDispatch(hideSlideOutPanel());
        } else {
            RouteHelper.getInstance().gotoEntityDetailsPage(props.projectId, entity.entityId);
        }
    }, [props, uiDispatch]);


    /**
     * On Create Child Entity button clicked, we will open the CREATE Entity view with homeId and parentId PRESELECTED.
     */
    const onCreateChildEntity = useCallback((entity: Entity) => {
        const action: EntityPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.ENTITY_PREVIEW,
            compProps: {
                data: {
                    projectId          : props.projectId,
                    preselectedHomeId  : entity.homeId,
                    preselectedParentId: entity.entityId,
                },
            },
        };
        uiDispatch(showSlideOutPanel(action));
    }, [props.projectId, uiDispatch]);


    /**
     * onDelete entity button clicked
     */
    const onDeleteEntity = useCallback((entity: Entity, deleteDescendants: boolean, errorMessage: string | undefined) => {

        // Confirm before deleting an Entity or Entity with its descendants
        // Because deleting an entity can be dangerous, we will ask the agent to enter the entity's name
        uiDispatch(showDialog({
            type     : DialogType.INPUT,
            title    : deleteDescendants ? 'label.delete_entity_and_descendants' : 'dialog.generic_confirm_delete.title',
            titleData: { object_name: entity.displaySettings.name, object_term: 'terms.entity' },
            message  : deleteDescendants ? 'dialog.confirm_delete_entity_and_descendants.message'
                : 'dialog.generic_confirm_delete.message',
            messageData   : { object_name: entity.displaySettings.name, object_term: 'terms.entity', object_field: 'label.name' },
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            input         : {
                type           : 'text',
                name           : 'label.name',
                error          : errorMessage,
                placeholder    : 'input.delete_object_name.hint_text',
                translationData: {
                    object_term : 'terms.entity',
                    object_field: 'label.name',
                },
                required: true,
            },
            onClose: async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    const entityName = response.input ? response.input.trim() : undefined;

                    if (!entityName) {
                        // Name is not provided. Call this function again to show the delete entity dialog with the new error
                        onDeleteEntity(entity, deleteDescendants, 'error.input.generic.empty');
                    } else if (entityName !== entity.displaySettings.name) {
                        // Name does not match. Call this function again to show the delete entity dialog with the new error
                        onDeleteEntity(entity, deleteDescendants, 'error.input.generic.invalid');
                    } else {
                        try {
                            await AppAPI.getInstance().deleteEntity(props.projectId, entity.entityId, deleteDescendants);

                            uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', {
                                term: capitalizeAll(trans('terms.entity')),
                            }));
                            unselectEntity();

                            // Clear all the cached entity data to force UI to reload entity list
                            await appDataDispatch(clearEntities(props.projectId));
                        } catch (error) {
                            // Show error using notification
                            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));

                            // Call this function again to show the delete entity dialog
                            onDeleteEntity(entity, deleteDescendants, undefined);
                        }
                    }
                }
            },
        }));
    }, [appDataDispatch, props.projectId, trans, uiDispatch, unselectEntity]);

    /**
     * Create an Array of primary actions for the Entity
     */
    const primaryActions = useMemo(() => {
        if (!isNewEntity && selectedEntity) {
            const actions: BaseCompAction[] = [{
                label  : capitalizeAll(trans('entity.action.show_entity_details', { term: trans('terms.entity') })),
                onClick: () => {
                    onShowEntityDetails(selectedEntity);
                },
            }];
            if (props.enableCreateChildren && canEditEntity) {
                actions.push({
                    label  : capitalizeAll(trans('entity.action.create_child_entity', { term: trans('terms.entity') })),
                    onClick: () => {
                        onCreateChildEntity(selectedEntity);
                    },
                });
            }
            return actions;
        }

        return undefined;
    }, [canEditEntity, isNewEntity, onCreateChildEntity, onShowEntityDetails, props.enableCreateChildren, selectedEntity, trans]);

    /**
     * Create secondary actions for the Entity
     */
    const secondaryActions = useMemo(() => {
        if (!isNewEntity && selectedEntity && props.enableDeleteEntity && canEditEntity) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'delete'),
                label    : capitalizeAll(trans('label.delete_object', { term: trans('terms.entity') })),
                icon     : 'delete',
                disabled : selectedEntityHasChildren,
                onClick  : () => {
                    onDeleteEntity(selectedEntity, false, undefined);
                },
            }, { className: clsx(classes.secondaryAction, 'delete'),
                label    : capitalizeAll(trans('label.delete_entity_and_descendants', { term: trans('terms.entity') })),
                icon     : 'delete',
                onClick  : () => {
                    onDeleteEntity(selectedEntity, true, undefined);
                } }];
            // if selectedDeviceId, option to view details
            if (selectedDeviceId) {
                actions.unshift(
                    {
                        className: clsx(classes.secondaryAction, 'device'),
                        label    : capitalizeAll(trans('label.view_device_details')),
                        icon     : trans('icons.device'),
                        onClick  : () => {
                            RouteHelper.getInstance(navigate).gotoDevicePage({ projectId, deviceId: selectedDeviceId });
                        },
                    },
                );
            }
            return actions;
        }
        return undefined;
    }, [isNewEntity, selectedEntity, props.enableDeleteEntity, canEditEntity, classes.secondaryAction,
        selectedEntityHasChildren, trans, selectedDeviceId, onDeleteEntity, navigate, projectId]);



    /**
     * Create the content for the slide-out panel. The content will just going to be a Entity
     */
    const slideOutCompContent = useMemo(() => {
        return (
            <EntityInfoContainer
                className="transparent"
                projectId={projectId}
                entityId={props.entityId}
                preselectedHomeId={props.preselectedHomeId}
                preselectedParentId={props.preselectedParentId}
                routeContext={props.routeContext}
                createEntityHandlers={createEntityHandlers}
            />
        );
    }, [createEntityHandlers, projectId, props.entityId, props.preselectedHomeId, props.preselectedParentId, props.routeContext]);



    return (
        <SlideOutComp
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            closable={!isNewEntity}
            hideActionBar={isEditing}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
